/**
 * Reject ID Dialog Component
 *
 * Allows admin to reject a recruit's ID document with a required reason.
 * Used when an ID document needs attention (blurry, expired, wrong type, etc.)
 *
 * Features:
 * - Warning message about consequences
 * - Required rejection reason (20-500 chars)
 * - Confirmation checkbox
 * - Triggers email notification to recruit
 */

import React, { useState, useEffect } from 'react';
import { LwModal } from '@/components/lw';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { rejectRecruitId, validateIdRejectionReason } from '@/services/recruitmentService';
import { useAuth } from '@/contexts/useAuth';

interface RejectIDDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  idType: 'primary' | 'secondary';
  recruitId: string;
  recruitName: string;
  recruitEmail: string;
  onRejectSuccess: () => void;
}

const MIN_REASON_LENGTH = 20;
const MAX_REASON_LENGTH = 500;

export const RejectIDDialog: React.FC<RejectIDDialogProps> = ({
  open,
  onOpenChange,
  idType,
  recruitId,
  recruitName,
  recruitEmail,
  onRejectSuccess
}) => {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setReason('');
      setConfirmed(false);
    }
  }, [open]);

  const handleReject = async () => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    // Validate reason
    const validation = validateIdRejectionReason(reason);
    if (!validation.valid) {
      toast.error(validation.message || 'Invalid rejection reason');
      return;
    }

    if (!confirmed) {
      toast.error('Please confirm you understand the consequences');
      return;
    }

    setIsRejecting(true);

    try {
      const result = await rejectRecruitId(
        recruitId,
        idType,
        user.id,
        reason.trim()
      );

      if (result.success) {
        toast.success(
          `${idType === 'primary' ? 'Primary' : 'Secondary'} ID rejected. Recruit will be notified.`
        );
        onOpenChange(false);
        onRejectSuccess();

        // TODO: Send email notification to recruit
        // This will be implemented via edge function
      }
    } catch (error) {
      console.error('Error rejecting ID:', error);
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsRejecting(false);
    }
  };

  const reasonLength = reason.trim().length;
  const isReasonValid = reasonLength >= MIN_REASON_LENGTH && reasonLength <= MAX_REASON_LENGTH;
  const canSubmit = isReasonValid && confirmed && !isRejecting;

  // Calculate remaining characters
  const remainingChars = MAX_REASON_LENGTH - reasonLength;

  const idTypeLabel = idType === 'primary' ? 'Primary ID' : 'Secondary ID';

  if (!open) {
    return null;
  }

  return (
    <LwModal
      eyebrow="ID review"
      title={`Reject ${idTypeLabel}`}
      sub={`Rejecting this ID will notify ${recruitName} (${recruitEmail}) to upload a new document.`}
      onClose={() => onOpenChange(false)}
      footer={
        <>
          <button className="btn-sm" onClick={() => onOpenChange(false)} disabled={isRejecting} type="button">
            Cancel
          </button>
          <button className="btn-sm danger" onClick={handleReject} disabled={!canSubmit} type="button">
            {isRejecting ? (
              <>
                <Loader2 className="animate-spin" />
                Rejecting…
              </>
            ) : (
              'Reject ID'
            )}
          </button>
        </>
      }
    >
      {/* Warning */}
      <div className="err-box">
        <span className="ic"><AlertTriangle /></span>
        <div>
          <div className="err-title">Rejecting this ID will:</div>
          <ul className="err-detail" style={{ listStyle: 'disc', paddingLeft: 18, marginTop: 4 }}>
            <li>Mark the {idTypeLabel.toLowerCase()} as rejected</li>
            <li>Send an email notification to the recruit with your reason</li>
            <li>Require the recruit to upload a new document</li>
            <li>Keep the onboarding task open until both IDs are approved</li>
          </ul>
        </div>
      </div>

      {/* Rejection Reason Input */}
      <div className="field" style={{ marginTop: 16 }}>
        <label htmlFor="rejection-reason">
          Reason for Rejection <span className="req">*</span>
        </label>
        <textarea
          id="rejection-reason"
          placeholder={`Please provide a clear reason for rejection (minimum ${MIN_REASON_LENGTH} characters)...\n\nExamples:\n- The document image is too blurry to verify\n- The ID appears to be expired (check expiration date)\n- Wrong type of ID uploaded - we need a government-issued photo ID\n- Name on ID doesn't match application`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={6}
          maxLength={MAX_REASON_LENGTH}
          className={reasonLength > 0 && !isReasonValid ? 'invalid' : ''}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, marginTop: 6 }}>
          <div>
            {reasonLength > 0 && (
              <span style={{ color: isReasonValid ? 'var(--green-700)' : 'var(--danger)', fontWeight: 600 }}>
                {reasonLength < MIN_REASON_LENGTH
                  ? `${MIN_REASON_LENGTH - reasonLength} more characters needed`
                  : '✓ Valid length'}
              </span>
            )}
          </div>
          <span style={{ color: remainingChars < 50 ? 'var(--danger)' : 'var(--lw-muted)' }}>
            {remainingChars} characters remaining
          </span>
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <div
        className="panel"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14, marginTop: 14 }}
      >
        <Checkbox
          id="confirm-reject"
          checked={confirmed}
          onCheckedChange={(checked) => setConfirmed(checked as boolean)}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="confirm-reject" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer', lineHeight: 1.2 }}>
            I understand the consequences
          </label>
          <p style={{ fontSize: 12, color: 'var(--lw-muted)', margin: 0 }}>
            I confirm that rejecting this ID is necessary and that the recruit will be notified to upload a new document.
          </p>
        </div>
      </div>
    </LwModal>
  );
};
